import React from 'react';
import { Check, Star, ArrowRight } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';

const LearnToRead = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Age-based pathways — matches the academy's readiness-first philosophy.
  const pathways = [
    {
      id: 'foundations',
      ageBand: '3–4 Years',
      title: 'Foundations',
      subtitle: 'Prepare • Listen • Explore',
      tagline:
        'A gentle first step into early literacy — building the listening, attention, movement and sound awareness that real reading is built on.',
      features: [
        'Listening & attention development',
        'Rhymes, songs and language play',
        'Vocabulary and oral expression',
        'Introduction to letter sounds',
        'Fine motor and pre-writing readiness',
        'Confidence and learning routines'
      ],
      bestFor:
        'Young children who are new to learning and need a calm, playful foundation before formal phonics begins.',
      buttonText: 'Start with Foundations',
      featured: false
    },
    {
      id: 'early-readers',
      ageBand: '4–5 Years',
      title: 'Early Readers',
      subtitle: 'Sounds • Blending • First Words',
      tagline:
        'Structured phonics, pure letter sounds and guided blending — the phase where children begin reading their very first words with real understanding.',
      features: [
        'Pure letter sounds (no “buh”, “kuh”, “muh”)',
        'Phonemic awareness practice',
        'Oral and written blending',
        'CVC word reading (cat, sun, pig…)',
        'Sight word introduction',
        'Vocabulary and comprehension games',
        'Writing readiness and letter formation'
      ],
      bonusFeatures: [
        'Monthly progress update for parents',
        'Simple home reading tips',
        'Reading milestone certificate'
      ],
      bestFor:
        'Children ready to move from letter recognition into real reading — blending sounds and reading simple words with support.',
      buttonText: 'Join Early Readers',
      featured: true
    },
    {
      id: 'school-ready',
      ageBand: '5–6 Years',
      title: 'School Ready',
      subtitle: 'Fluency • Comprehension • Confidence',
      tagline:
        'The next stage of early reading — blends, digraphs, sentence reading, comprehension and the confidence to read aloud independently.',
      features: [
        'Consonant blends and digraphs (sh, ch, th, bl…)',
        'Sentence reading and simple stories',
        'Reading fluency and expression',
        'Comprehension and meaning',
        'Structured writing readiness',
        'Speaking, listening and confidence building',
        'Preparation for school-based reading'
      ],
      bestFor:
        'Children preparing for school or those who need to strengthen fluency, comprehension and reading confidence.',
      buttonText: 'Prepare for School',
      featured: false
    }
  ];

  return (
    <section id="programs" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-slate-50">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block bg-blue-50 text-[#3B4BA0] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Children&apos;s Reading Pathways (3–6 Years)
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Learn to Read
            <span className="block text-[#3B4BA0] text-2xl sm:text-3xl mt-2 font-medium">
              Age-based pathways that meet your child where they are
            </span>
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            At Read Right Academy, every child is guided according to their readiness,
            pace and confidence. Choose the pathway that matches your child&apos;s
            current stage — or start with a free trial and we&apos;ll recommend the
            best fit.
          </p>
        </div>

        {/* Age-based Cards */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {pathways.map((pkg) => (
            <Card
              key={pkg.id}
              id={pkg.id}
              data-testid={`pathway-card-${pkg.id}`}
              className={`relative p-8 transition-all duration-300 flex flex-col scroll-mt-24 ${
                pkg.featured
                  ? 'border-2 border-[#3B4BA0] shadow-2xl scale-105 bg-white lg:-mt-4'
                  : 'border-2 border-gray-100 hover:border-[#3B4BA0] hover:shadow-xl bg-white'
              }`}
            >
              {pkg.featured && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-[#3B4BA0] to-teal-500 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg flex items-center gap-1 whitespace-nowrap">
                  <Star className="h-4 w-4 fill-current" />
                  Most Popular
                </div>
              )}

              {/* Age band pill */}
              <div className="mb-4">
                <span className="inline-block bg-gradient-to-r from-blue-50 to-teal-50 text-[#3B4BA0] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border border-blue-100">
                  {pkg.ageBand}
                </span>
              </div>

              {/* Header */}
              <div className="mb-6 pb-6 border-b border-gray-100">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  {pkg.title}
                </h3>
                <p className="text-sm text-[#3B4BA0] font-semibold mb-3">
                  {pkg.subtitle}
                </p>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {pkg.tagline}
                </p>
              </div>

              {/* Features */}
              <div className="mb-6 flex-grow">
                <p className="text-sm font-semibold text-gray-900 mb-4">What&apos;s included:</p>
                <ul className="space-y-3">
                  {pkg.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-teal-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                {pkg.bonusFeatures && (
                  <div className="mt-6 pt-6 border-t border-gray-100">
                    <p className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <Star className="h-4 w-4 text-amber-500 fill-current" />
                      Parent Perks:
                    </p>
                    <ul className="space-y-3">
                      {pkg.bonusFeatures.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <Check className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Best For */}
              <div className="mb-6 bg-blue-50 rounded-lg p-4 border-l-4 border-[#3B4BA0]">
                <p className="text-xs font-semibold text-[#3B4BA0] mb-1 uppercase tracking-wide">Best For</p>
                <p className="text-sm text-gray-700 leading-relaxed">
                  {pkg.bestFor}
                </p>
              </div>

              {/* CTA Button */}
              <Button
                onClick={scrollToContact}
                data-testid={`pathway-cta-${pkg.id}`}
                className={`w-full py-6 text-base font-semibold transition-all duration-300 ${
                  pkg.featured
                    ? 'bg-[#3B4BA0] hover:bg-[#2d3a7a] text-white shadow-lg'
                    : 'bg-white border-2 border-[#3B4BA0] text-[#3B4BA0] hover:bg-[#3B4BA0] hover:text-white'
                }`}
              >
                {pkg.buttonText}
              </Button>
            </Card>
          ))}
        </div>

        {/* Class formats note */}
        <div className="mb-16 bg-white rounded-2xl p-6 sm:p-8 border-2 border-gray-100 shadow-sm">
          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 text-center mb-6">
            Available in Formats That Fit Your Child
          </h3>
          <div className="grid sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="text-center p-5 rounded-xl bg-slate-50">
              <p className="text-sm font-bold text-[#3B4BA0] uppercase tracking-wider mb-2">Free Trial</p>
              <p className="text-sm text-gray-600 leading-relaxed">
                A gentle 45-minute observation to recommend the right starting point.
              </p>
            </div>
            <div className="text-center p-5 rounded-xl bg-slate-50">
              <p className="text-sm font-bold text-[#3B4BA0] uppercase tracking-wider mb-2">One-to-One</p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Personalised lessons paced to your child&apos;s readiness and confidence.
              </p>
            </div>
            <div className="text-center p-5 rounded-xl bg-slate-50">
              <p className="text-sm font-bold text-[#3B4BA0] uppercase tracking-wider mb-2">Read Together (Group)</p>
              <p className="text-sm text-gray-600 leading-relaxed">
                Small guided groups of 6–10 children learning with friends.
              </p>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="bg-gradient-to-r from-[#3B4BA0] to-teal-600 rounded-2xl p-10 text-center text-white shadow-xl">
          <h3 className="text-3xl font-bold mb-4">
            Not Sure Which Pathway Fits Your Child?
          </h3>
          <p className="text-lg text-blue-50 mb-8 max-w-2xl mx-auto">
            Start with a <strong>Free Trial Class</strong>. We&apos;ll gently observe
            your child&apos;s readiness, confidence and early reading skills, then
            recommend the most suitable pathway.
          </p>
          <Button
            size="lg"
            onClick={scrollToContact}
            data-testid="book-trial-cta"
            className="bg-white text-[#3B4BA0] hover:bg-gray-100 text-lg px-10 py-6 shadow-lg hover:shadow-xl transition-all duration-300 group"
          >
            Book a Free Trial Today
            <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default LearnToRead;
